import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { BandBadge } from "../components/Badges";
import { CalendarClock, AlertCircle, Clock, X, Info } from "lucide-react";

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay());
  return x;
}

function iso(d) {
  return new Date(d).toISOString().slice(0, 10);
}

export default function NurtureCalendar() {
  const { user } = useAuth();
  const canFilter = user.role !== "rep";
  const [leads, setLeads] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [reps, setReps] = useState([]);
  const [repFilter, setRepFilter] = useState("");
  const nav = useNavigate();

  const load = () => {
    setLeads(null);
    api.get(`/nurture${repFilter ? `?assigned_to=${repFilter}` : ""}`).then((r) => setLeads(r.data));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [repFilter]);
  useEffect(() => {
    if (canFilter) api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "rep"))).catch(() => {});
  }, [canFilter]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const todayIso = iso(new Date());

  const byDay = (dayIso) => (leads || []).filter((l) => l.nurture_date === dayIso);
  const overdue = (leads || []).filter((l) => l.nurture_date && l.nurture_date < iso(weekStart));
  const shiftWeek = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(d); };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const applyDates = (updated) => {
    const map = Object.fromEntries(updated.map((u) => [u.id, u.nurture_date]));
    setLeads((ls) => ls.map((x) => (map[x.id] ? { ...x, nurture_date: map[x.id] } : x)));
  };

  const snooze = async (e, l) => {
    e.stopPropagation();
    try {
      const r = await api.post("/nurture/bulk-snooze", { lead_ids: [l.id], weeks: 1 });
      applyDates(r.data.leads);
      toast.success(`${l.account?.name || "Lead"} pushed +1 week`);
    } catch { toast.error("Snooze failed"); }
  };

  const bulkSnooze = async () => {
    setBusy(true);
    try {
      const r = await api.post("/nurture/bulk-snooze", { lead_ids: [...selected], weeks: 1, reason: reason.trim() || undefined });
      applyDates(r.data.leads);
      toast.success(`${r.data.updated} lead${r.data.updated !== 1 ? "s" : ""} pushed +1 week`);
      setSelected(new Set());
      setReason("");
    } catch { toast.error("Bulk snooze failed"); } finally { setBusy(false); }
  };

  const Check = ({ id }) => (
    <input type="checkbox" checked={selected.has(id)} onClick={(e) => toggleSelect(e, id)} onChange={() => {}}
      data-testid={`select-${id}`} className="w-3.5 h-3.5 accent-[#3E8F99] cursor-pointer" />
  );

  return (
    <>
      <PageHeader
        eyebrow="NURTURE TRACK · CHECK-BACK-LATER TIMELINE"
        title="Nurture Calendar"
        right={
          <div className="flex items-center gap-2" data-testid="nurture-week-nav">
            {canFilter && (
              <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} data-testid="nurture-rep-filter"
                className="border border-[#DFDAD0] px-2 py-1.5 font-mono text-[10px] uppercase outline-none focus:border-[#1C2530] bg-white">
                <option value="">All reps</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            )}
            <button onClick={() => shiftWeek(-1)} data-testid="nurture-prev-week" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]">← Prev</button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]">This week</button>
            <button onClick={() => shiftWeek(1)} data-testid="nurture-next-week" className="border border-[#DFDAD0] px-3 py-1.5 font-mono text-[10px] uppercase hover:bg-[#EFECE5]">Next →</button>
          </div>
        }
      />
      <div className="p-8">
        <div className="mb-5 border-l-2 border-[#3E8F99] bg-[#3E8F99]/5 px-4 py-3 text-sm text-[#1C2530]/80 flex flex-col md:flex-row md:items-center gap-2 md:gap-6" data-testid="nurture-explainer">
          <div className="flex items-start gap-2"><Info className="w-4 h-4 text-[#3E8F99] shrink-0 mt-0.5" />
            <span><span className="font-medium text-[#1C2530]">How this works:</span> a lead lands here when someone opens it and clicks <span className="font-medium">Move to Nurture</span> with a check-back date. When that date arrives the lead is <span className="font-medium">surfaced</span> — it stays in nurture, but shows up in your Overdue bucket, dashboard and daily digest so you can re-engage. Use <span className="font-medium">Push +1 week</span> to reschedule.</span>
          </div>
          <button onClick={() => nav(user.role === "rep" ? "/queue" : "/pipeline")} data-testid="nurture-goto-queue" className="font-mono text-[10px] uppercase tracking-wider text-[#3E8F99] hover:underline whitespace-nowrap self-start md:self-auto">Open a lead to set a date →</button>
        </div>
        {selected.size > 0 && (
          <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3 bg-[#1C2530] text-[#F5F3EE] px-4 py-3" data-testid="bulk-snooze-bar">
            <span className="font-mono text-[11px] uppercase tracking-wider whitespace-nowrap">{selected.size} selected</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="bulk-reason-input"
              placeholder="Shared reason (optional) — e.g. budget frozen till Q3"
              className="flex-1 bg-[#F5F3EE]/10 border border-[#F5F3EE]/20 px-3 py-1.5 text-sm text-[#F5F3EE] placeholder-[#F5F3EE]/40 outline-none focus:border-[#3E8F99]" />
            <div className="flex items-center gap-2">
              <button onClick={bulkSnooze} disabled={busy} data-testid="bulk-snooze-btn"
                className="flex items-center gap-1.5 bg-[#3E8F99] px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider hover:bg-[#357a83] disabled:opacity-50 whitespace-nowrap">
                <Clock className="w-3 h-3" /> Push all +1 week
              </button>
              <button onClick={() => { setSelected(new Set()); setReason(""); }} data-testid="bulk-clear-btn" className="text-[#F5F3EE]/60 hover:text-[#F5F3EE]"><X className="w-4 h-4" /></button>
            </div>
          </div>
        )}
        {!leads ? <Loading /> : leads.length === 0 ? <Empty label="NO_NURTURE_LEADS" /> : (
          <>
            {overdue.length > 0 && (
              <div className="mb-6 border border-[#B23A2E]/40 bg-[#B23A2E]/5 p-4" data-testid="nurture-overdue">
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[#B23A2E] mb-2">
                  <AlertCircle className="w-3.5 h-3.5" /> Overdue re-engagement ({overdue.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {overdue.map((l) => (
                    <div key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`nurture-lead-${l.id}`}
                      className="flex items-center gap-2 border border-[#B23A2E]/30 bg-white px-3 py-1.5 hover:bg-[#EFECE5] cursor-pointer">
                      <Check id={l.id} />
                      <div>
                        <div className="text-sm font-medium text-[#1C2530]">{l.account?.name || l.contact?.name || "Lead"}</div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[9px] text-[#B23A2E]">due {l.nurture_date}</span>
                          <button onClick={(e) => snooze(e, l)} data-testid={`snooze-${l.id}`}
                            className="flex items-center gap-1 font-mono text-[9px] uppercase text-[#3E8F99] hover:underline">
                            <Clock className="w-2.5 h-2.5" /> +1 week
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="nurture-week-grid">
              {days.map((d) => {
                const di = iso(d);
                const items = byDay(di);
                const isToday = di === todayIso;
                return (
                  <div key={di} className={`border bg-white min-h-[160px] ${isToday ? "border-[#3E8F99]" : "border-[#DFDAD0]"}`}>
                    <div className={`px-2 py-2 border-b ${isToday ? "border-[#3E8F99] bg-[#3E8F99]/5" : "border-[#DFDAD0] bg-[#EFECE5]"}`}>
                      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/70">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                      <div className="font-mono text-xs text-[#1C2530]">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    </div>
                    <div className="p-2 space-y-2">
                      {items.length === 0 ? (
                        <div className="font-mono text-[9px] text-[#1C2530]/30">—</div>
                      ) : items.map((l) => (
                        <div key={l.id} onClick={() => nav(`/leads/${l.id}`)} data-testid={`nurture-lead-${l.id}`}
                          className="w-full text-left border border-[#DFDAD0] p-2 hover:bg-[#EFECE5] transition-colors cursor-pointer">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Check id={l.id} />
                            <CalendarClock className="w-3 h-3 text-[#3E8F99]" />
                            <span className="text-xs font-medium text-[#1C2530] truncate">{l.account?.name || l.contact?.name || "Lead"}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-[9px] text-[#1C2530]/50 truncate">{l.assignee_name || "Unassigned"}</span>
                            <BandBadge band={l.band} score={l.total_score} />
                          </div>
                          <button onClick={(e) => snooze(e, l)} data-testid={`snooze-${l.id}`}
                            className="mt-1.5 w-full flex items-center justify-center gap-1 border border-[#DFDAD0] py-1 font-mono text-[9px] uppercase text-[#3E8F99] hover:bg-white">
                            <Clock className="w-2.5 h-2.5" /> Push +1 week
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}
